// Section headers (dashboard layout): a section with tabbed views shares one title row on its list pages, the
// section's name with its views as tabs; any other list page in the rail is its title alone. Pages under a section
// that are not one of its views (an object, a create flow, a secondary list) lead their title with the view or the
// section as a muted crumb instead.
import { ActionsMenu, LinkTabs, PageHeader, toast, type MenuAction } from "@nodus/ui";
import { useLocation } from "@tanstack/react-router";
import * as React from "react";
import { useServedKinds } from "./discovery";
import { activeChild, activeNav, activeTab, isSectionView, navHref, type NavItem, type NavTab } from "./nav";
import { useOrg, useShellProject } from "./shell-state";

export function useVisibleTabs(item: NavItem | undefined): NavTab[] {
  const { org } = useOrg();
  const served = useServedKinds(org.name);
  return (item?.tabs ?? []).filter((t) => !t.servedKind || served?.has(t.servedKind));
}

export function useSection() {
  const { pathname, searchStr } = useLocation();
  const item = activeNav(pathname);
  return { item, tab: activeTab(item, pathname, searchStr), child: activeChild(item, pathname, searchStr), view: isSectionView(item, pathname) };
}

/** The section's views as tabs, for the section's list pages. */
export function SectionTabs({ item, active }: { item: NavItem; active: string }) {
  const { org } = useOrg();
  const project = useShellProject();
  const tabs = useVisibleTabs(item);
  return <LinkTabs label={item.label} active={active} tabs={tabs.map((t) => ({ id: t.id, label: t.label, href: navHref(t, org.name, project) }))} />;
}

/**
 * The title row of every section page: on a tabbed section's view, the section's name and its tabs; on any other list
 * page, the title alone; elsewhere, `View / Title` (or `Section / Title`). The CLI command is available in the page actions menu.
 */
export function SectionHeader({
  title,
  description,
  cli,
  actions,
  views,
  secondaryActions = [],
}: {
  title: string;
  description?: string;
  cli?: string;
  actions?: React.ReactNode;
  secondaryActions?: MenuAction[];
  /** Draw the section tabs even off a view path (a frame such as Billing that owns sub-routes). */
  views?: boolean;
}) {
  const { org } = useOrg();
  const project = useShellProject();
  const { item, tab, child, view } = useSection();
  const tabs = useVisibleTabs(item);
  const menu: MenuAction[] = [
    ...secondaryActions,
    ...(cli ? [{ label: "Copy CLI command", separatorBefore: secondaryActions.length > 0, onSelect: () => {
      void navigator.clipboard.writeText(cli).then(() => toast("CLI command copied")).catch(() => toast.error("Could not copy command"));
    } }] : []),
  ];
  const headerActions = <>{actions}{menu.length ? <ActionsMenu label="Page actions" actions={menu} /> : null}</>;
  if (item && tab && tabs.length > 1 && (views ?? view)) {
    return (
      <PageHeader title={item.label} description={description} cli={cli} actions={headerActions}>
        <SectionTabs item={item} active={tab.id} />
      </PageHeader>
    );
  }
  if (view) return <PageHeader title={title} description={description} cli={cli} actions={headerActions} />;
  const parent = child ?? (tab && tabs.length > 1 ? tab : item);
  const parents = parent && parent.label !== title ? [{ label: parent.label, href: navHref(parent, org.name, project) }] : undefined;
  return <PageHeader title={title} description={description} cli={cli} actions={headerActions} {...(parents ? { parents } : {})} />;
}
